import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { getUserDetailsBaseUrl } from '../utils/chatIdentity';
import { withoutEmDashes } from '../utils/displayText';
import { formatMileage, unitLabel, toMiles, MAX_ODOMETER_MILES, ODOMETER_TOO_HIGH_MESSAGE } from '../utils/distance';
import USDateInput from './USDateInput';
import {
    DOCUMENT_CATEGORY_DEFINITIONS,
    buildPublicManualLinks,
    formatTimeAgo,
    getDocumentDateLabel,
    getDocumentSearchText,
    getDocumentStatus,
    getDocumentTimestamp,
    getDocumentVehicleId,
    getLatestDocumentForCategory,
    inferDocumentCategory,
    isTicketClosed,
    resolveTicketDisplayTitle,
    ticketPathLabel,
    ticketRealId,
    ticketThreadId,
    ticketTitle,
    ticketVehicleLabel,
} from '../utils/coreAppEnhancements';

const DOCUMENTS_PREVIEW_COUNT = 8;
const HISTORY_PREVIEW_COUNT = 8;

// Stable id for a document row ('' when it has none) — used to find the one just uploaded.
const getDocId = (doc) => String(doc?.doc_id ?? doc?.docId ?? '');

const getVehicleLabel = (vehicle) => (
    [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(' ') || vehicle?.vehicle_label || vehicle?.vehicleLabel || ''
);

const getDocumentTitle = (doc) => withoutEmDashes((
    doc?.original_filename || doc?.originalFilename || doc?.name || 'Untitled document'
).replace(/\.[^/.]+$/, ''));

const getDocumentFileUrl = (doc) => {
    if (doc?.doc_id || doc?.docId) {
        return `${getUserDetailsBaseUrl()}/api/documents/me/${encodeURIComponent(doc.doc_id || doc.docId)}/file`;
    }
    return doc?.url || doc?.storage_url || doc?.storageUrl || '';
};

// A generated diagnostic report lives in the app (not a real file), so it's opened in-app
// (navigate to the report view) rather than via the document /file endpoint.
const isDiagnosticReportDoc = (doc) =>
    String(doc?.document_kind || doc?.documentKind || '').toLowerCase() === 'diagnostic_report';

const getDiagnosticReportRef = (doc) => {
    const ref = String(doc?.storage_url || doc?.storageUrl || doc?.path || '');
    const match = /thread-(\d+)-diagnostic-report/.exec(ref);
    return {
        reportId: ref || '',
        threadId: match ? match[1] : (doc?.diagnostic_ticket_id ? String(doc.diagnostic_ticket_id) : ''),
    };
};

const getServiceAttachmentUrl = (attachment, phoneNumber) => {
    const rawUrl = attachment?.url || attachment?.storage_url || attachment?.storageUrl || '';
    const attachmentId = attachment?.attachment_id || attachment?.attachmentId;
    if (phoneNumber && attachmentId && (!rawUrl || String(rawUrl).startsWith('s3://'))) {
        return `${getUserDetailsBaseUrl()}/api/service-history/${encodeURIComponent(phoneNumber)}/attachments/${encodeURIComponent(attachmentId)}`;
    }
    return rawUrl;
};

const isImageDocument = (doc) => {
    const mimeType = String(doc?.file_type || doc?.mime_type || '').toLowerCase();
    const mediaKind = String(doc?.media_kind || doc?.mediaKind || '').toLowerCase();
    const filename = String(doc?.original_filename || doc?.name || '').toLowerCase();
    return mimeType.startsWith('image/')
        || mediaKind === 'photo'
        || mediaKind === 'image'
        || /\.(avif|bmp|gif|hei[cf]|jpe?g|jfif|png|tiff?|webp)$/i.test(filename);
};

const categoryIconPath = (categoryId) => {
    switch (categoryId) {
        case 'registration':
            return <><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 9h8M8 13h5M8 17h7" /></>;
        case 'insurance':
            return <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-5" /></>;
        case 'manual':
            return <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5z" /></>;
        case 'service_record':
            return <><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 0 0 5.4-5.4l-2.3 2.3-2.4-.6-.6-2.4z" /></>;
        case 'invoice':
            return <><path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 1 .7V2z" /><path d="M8 7h8M8 11h8M8 15h5" /></>;
        case 'estimate':
            return <><path d="M9 11h6M9 15h4" /><path d="M16 3H8a2 2 0 0 0-2 2v16l6-3 6 3V5a2 2 0 0 0-2-2z" /></>;
        case 'obd_report':
            return <><path d="M3 12h4l2-6 4 12 2-6h6" /></>;
        case 'ai_report':
            return <><path d="M12 3l1.6 4.8L18 9.4l-4.4 1.6L12 16l-1.6-5L6 9.4l4.4-1.6z" /><path d="M5 19h14" /></>;
        default:
            return <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>;
    }
};

const DocumentIcon = ({ categoryId }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        {categoryIconPath(categoryId)}
    </svg>
);

const HistoryIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 3" />
    </svg>
);

const WrenchIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 0 0 5.4-5.4l-2.3 2.3-2.4-.6-.6-2.4z" />
    </svg>
);

const Badge = ({ children, className = '' }) => (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${className}`}>
        {children}
    </span>
);

const GloveboxTabs = ({ active, onChange }) => {
    const tabs = [
        { id: 'history', label: 'History' },
        { id: 'documents', label: 'Documents' },
    ];
    return (
        <div className="inline-flex rounded-xl border border-[#d9d9d9] bg-white p-1.5 shadow-sm">
            {tabs.map((tab) => (
                <button
                    key={tab.id}
                    type="button"
                    onClick={() => onChange(tab.id)}
                    className={`rounded-lg px-8 py-2.5 text-sm font-black transition ${
                        active === tab.id ? 'bg-[#414141] text-white shadow-sm' : 'text-[#414141] hover:text-black'
                    }`}
                >
                    {tab.label}
                </button>
            ))}
        </div>
    );
};

const CategoryPills = ({ value, onChange }) => (
    <div className="flex gap-2 overflow-x-auto pb-1">
        {DOCUMENT_CATEGORY_DEFINITIONS.map((category) => (
            <button
                key={category.id}
                type="button"
                onClick={() => onChange(category.id)}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-black transition ${
                    value === category.id
                        ? 'border-[#414141] bg-[#414141] text-white'
                        : 'border-[#d9d9d9] bg-white text-[#535353] hover:border-[#414141]'
                }`}
            >
                {category.shortLabel}
            </button>
        ))}
    </div>
);

// The pilot promo reward (auth/promo.py) is a record, not a file — nothing to open.
const RewardListItem = ({ doc }) => {
    const { expires_label: expiresLabel, description } = doc?.extracted_markers || {};
    return (
        <div className="px-4 py-3.5 md:px-5">
            <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] text-[#414141]">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="3" y="8" width="18" height="4" rx="1" />
                        <path d="M12 8v13" />
                        <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
                        <path d="M7.5 8a2.5 2.5 0 0 1 0-5C10 3 12 8 12 8s2-5 4.5-5a2.5 2.5 0 0 1 0 5" />
                    </svg>
                </div>
                <div className="min-w-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                        <h3 className="min-w-0 max-w-full truncate text-sm font-black text-black">{getDocumentTitle(doc)}</h3>
                        <Badge className="bg-emerald-50 text-emerald-700">Active</Badge>
                    </div>
                    {description && <p className="mt-1 text-xs font-semibold text-[#414141]">{description}</p>}
                    {expiresLabel && <p className="mt-1 text-xs font-bold text-[#414141]">Expires: {expiresLabel}</p>}
                    <p className="mt-2 text-xs font-normal leading-relaxed text-[#414141]">
                        <strong>Terms:</strong> First 50 eligible participants only. Minimum $100 paid repair/service at a participating CarFido partner shop. A valid paid service invoice must be uploaded to CarFido for verification. Limit one reward per person. Expires 12/31/26. Subject to verification; fraudulent or incomplete claims are ineligible. CarFido may modify or end the offer as permitted by law.
                    </p>
                </div>
            </div>
        </div>
    );
};

const DocumentListItem = ({ doc, onOpenImage, onOpenReport }) => {
    if (doc?.document_kind === 'pilot_reward') return <RewardListItem doc={doc} />;
    const category = inferDocumentCategory(doc);
    const status = getDocumentStatus(doc);
    const title = getDocumentTitle(doc);
    const isReportDoc = isDiagnosticReportDoc(doc);
    // A diagnostic-report doc opens in-app, not as a file — so it's always "openable".
    const href = isReportDoc ? '' : getDocumentFileUrl(doc);
    const isImage = !isReportDoc && isImageDocument(doc);
    const sourceLabel = doc?.source === 'agent_generated' ? 'CarFido generated' : 'Uploaded';
    const metaLabel = [category.label, sourceLabel].filter(Boolean).join(' / ');

    const content = (
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex min-w-0 items-start gap-3">
                {isImage && href ? (
                    <img
                        src={href}
                        alt=""
                        className="h-10 w-10 shrink-0 rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] object-cover transition-colors group-hover:border-[#B3B3B3]"
                    />
                ) : (
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] text-[#414141] transition-colors group-hover:border-[#B3B3B3] group-hover:bg-[#f5f5f5]">
                        <DocumentIcon categoryId={category.id} />
                    </div>
                )}
                <div className="min-w-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                        <h3 className="min-w-0 max-w-full truncate text-sm font-black text-black">{title}</h3>
                        <Badge className={status.classes}>{status.label}</Badge>
                    </div>
                    <p className="mt-1 max-w-full truncate text-xs font-semibold text-[#414141]">{metaLabel}</p>
                </div>
            </div>
            <div className="flex shrink-0 items-center justify-between gap-3 md:w-[190px] md:justify-end">
                <span className="text-xs font-bold text-[#414141]">{getDocumentDateLabel(doc)}</span>
                <span className={`inline-flex min-h-[32px] items-center gap-1.5 rounded-xl border px-3 text-xs font-black transition ${
                    (href || isReportDoc)
                        ? 'border-[#d9d9d9] bg-white text-[#414141] group-hover:border-[#D81F26] group-hover:text-[#D81F26]'
                        : 'border-[#ececec] bg-[#F9F9F9] text-[#414141]'
                }`}>
                    {(href || isReportDoc) ? 'Open' : 'On file'}
                    {(href || isReportDoc) && (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 3h6v6" />
                            <path d="M10 14 21 3" />
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        </svg>
                    )}
                </span>
            </div>
        </div>
    );

    if (isReportDoc) {
        return (
            <button
                type="button"
                onClick={() => onOpenReport?.(doc)}
                className="group block w-full px-4 py-3.5 text-left transition-all hover:bg-[#F9F9F9] md:px-5"
            >
                {content}
            </button>
        );
    }

    if (!href) {
        return (
            <div className="group px-4 py-3.5 opacity-80 md:px-5">
                {content}
            </div>
        );
    }

    if (isImage) {
        return (
            <button
                type="button"
                onClick={() => onOpenImage?.({ ...doc, url: href })}
                className="group block w-full px-4 py-3.5 text-left transition-all hover:bg-[#F9F9F9] md:px-5"
            >
                {content}
            </button>
        );
    }

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="group block px-4 py-3.5 transition-all hover:bg-[#F9F9F9] md:px-5"
        >
            {content}
        </a>
    );
};

const DocumentQuickCards = ({
    cards,
    manualDocs = [],
    manualLinks = [],
    onOpenDocument,
    onUpload,
    uploading = false,
    newDocId = null,
}) => (
    <section className="grid gap-3 md:grid-cols-3">
        {cards.map((card) => (
            <div
                key={card.id}
                className={`rounded-2xl border border-[#d9d9d9] bg-white px-4 py-3.5 shadow-sm ${
                    newDocId && card.doc && getDocId(card.doc) === newDocId ? 'animate-dash-new-glow' : ''
                }`}
            >
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] text-[#414141]">
                        <DocumentIcon categoryId={card.id} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <h3 className="text-sm font-black text-black">{card.label}</h3>
                            {card.status && <Badge className={card.status.classes}>{card.status.label}</Badge>}
                        </div>
                        <p className="mt-1 min-h-[32px] text-xs font-semibold leading-relaxed text-[#414141]">{card.summary}</p>
                    </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                    {card.doc ? (
                        <button
                            type="button"
                            onClick={() => onOpenDocument(card.doc)}
                            aria-label={`Open latest ${card.label}`}
                            className="inline-flex min-h-[34px] items-center rounded-full border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26]"
                        >
                            Open latest
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => onUpload(card.id)}
                            disabled={uploading}
                            aria-label={`Upload new ${card.label}`}
                            className="inline-flex min-h-[34px] items-center rounded-full border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26]"
                        >
                            Upload new
                        </button>
                    )}
                    {card.id !== 'manual' && card.doc && card.needsUpload && (
                        <button
                            type="button"
                            onClick={() => onUpload(card.id)}
                            disabled={uploading}
                            aria-label={`Upload new ${card.label}`}
                            className="inline-flex min-h-[34px] items-center rounded-full bg-[#414141] px-3 text-xs font-black text-white transition hover:bg-[#2A2A2A]"
                        >
                            Upload new
                        </button>
                    )}
                </div>

                {card.id === 'manual' && (manualDocs.length > 0 || manualLinks.length > 0) && (
                    <div className="mt-3 space-y-1.5 border-t border-[#d9d9d9] pt-3">
                        {manualDocs.slice(0, 2).map(({ doc }) => (
                            <button
                                key={doc.doc_id || doc.path || doc.name}
                                type="button"
                                onClick={() => onOpenDocument(doc)}
                                className="block max-w-full truncate text-left text-xs font-black text-[#D81F26] hover:underline"
                            >
                                {getDocumentTitle(doc)}
                            </button>
                        ))}
                        {manualLinks.map((link) => (
                            <a
                                key={link.url}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block max-w-full truncate text-xs font-black text-[#D81F26] hover:underline"
                            >
                                {link.title}
                            </a>
                        ))}
                    </div>
                )}
            </div>
        ))}
    </section>
);

const DocumentImageModal = ({ doc, onClose }) => {
    if (!doc) return null;
    const src = doc.url || getDocumentFileUrl(doc);
    if (!src) return null;
    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 px-4 py-6" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="relative max-h-full w-full max-w-5xl" onClick={(event) => event.stopPropagation()}>
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-white transition hover:bg-black"
                    aria-label="Close preview"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                    </svg>
                </button>
                <img src={src} alt={getDocumentTitle(doc)} className="mx-auto max-h-[82vh] max-w-full rounded-2xl bg-white object-contain shadow-2xl" />
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white px-4 py-3">
                    <p className="min-w-0 truncate text-sm font-black text-black">{getDocumentTitle(doc)}</p>
                    <a href={src} target="_blank" rel="noopener noreferrer" className="shrink-0 rounded-full bg-[#D81F26] px-4 py-2 text-xs font-black text-white hover:bg-[#B8181E]">
                        Open in new tab
                    </a>
                </div>
            </div>
        </div>
    );
};

const formatServiceDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatServiceCost = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: amount % 1 === 0 ? 0 : 2 });
};

const formatProviderLabel = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const normalized = raw.toLowerCase();
    if (normalized === 'owner' || normalized === 'self' || normalized === 'diy') return 'Owner / DIY';
    if (normalized === 'shop') return 'Shop service';
    if (normalized === 'dealer') return 'Dealer service';
    return raw;
};

const getServiceVehicleLabel = (entry) => (
    entry?.vehicle ||
    [entry?.vehicle_year, entry?.vehicle_make, entry?.vehicle_model, entry?.vehicle_trim].filter(Boolean).join(' ')
);

const ServiceDetail = ({ label, value }) => {
    if (!value) return null;
    return (
        <div className="rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 py-2">
            <p className="text-[10px] font-black uppercase tracking-widest text-[#414141]">{label}</p>
            <p className="mt-0.5 break-words text-xs font-black text-[#535353]">{value}</p>
        </div>
    );
};

// A labelled block in the expanded service record — used for symptoms, work performed, parts,
// notes, attachments, related records, and recommended maintenance.
const ServiceSection = ({ label, children }) => (
    <div className="rounded-xl border border-[#d9d9d9] bg-white px-3 py-2.5">
        <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-[#414141]">{label}</p>
        <div className="text-xs font-semibold leading-relaxed text-[#414141]">{children}</div>
    </div>
);

const getClosedTicketStatusMeta = (ticket) => {
    const status = String(ticket?.status || '').toLowerCase();
    const outcome = String(ticket?.outcome || '').toLowerCase();
    if (status === 'abandoned' || outcome === 'abandoned') {
        return { label: 'Abandoned', classes: 'bg-[#f3f3f3] text-[#414141]' };
    }
    if (outcome === 'successful') {
        return { label: 'Resolved', classes: 'bg-emerald-50 text-emerald-700' };
    }
    return { label: 'Unresolved', classes: 'bg-amber-50 text-amber-700' };
};

const DiagnosticHistoryCard = ({ ticket, onOpen, onAddServiceRecord }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const statusMeta = getClosedTicketStatusMeta(ticket);
    const pathLabel = ticketPathLabel(ticket.path);
    const closedLabel = formatTimeAgo(ticket.closed_at || ticket.resolved_at || ticket.last_activity_at);
    const openedLabel = formatTimeAgo(ticket.opened_at);
    const resolutionSummary = (ticket.summary || '').trim();
    const originalIssue = (ticket.symptom_summary || '').trim();
    const subheader = resolutionSummary || 'No repair notes recorded for this case.';
    const attachments = Array.isArray(ticket.attachments) ? ticket.attachments.filter(Boolean) : [];

    return (
        <div className="rounded-2xl border border-[#d9d9d9] bg-white px-4 py-3.5 shadow-sm transition hover:border-[#B3B3B3] md:px-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <button
                    type="button"
                    onClick={() => onOpen(ticket)}
                    className="group flex min-w-0 flex-1 items-start gap-3 text-left"
                >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] text-[#414141] transition-colors group-hover:border-[#B3B3B3] group-hover:bg-[#f5f5f5]">
                        <HistoryIcon />
                    </div>
                    <div className="min-w-0">
                        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                            <h3 className="min-w-0 max-w-full truncate text-sm font-black text-black group-hover:underline">{ticket._displayTitle}</h3>
                            <Badge className={statusMeta.classes}>{statusMeta.label}</Badge>
                            {pathLabel && <Badge className="bg-[#F2F2F2] text-[#414141]">{pathLabel}</Badge>}
                        </div>
                        <p className="mt-1 max-w-full truncate text-xs font-semibold text-[#414141]">{subheader}</p>
                    </div>
                </button>
                <div className="flex shrink-0 items-center gap-2 self-start">
                    {closedLabel && <span className="text-xs font-bold text-[#414141]">{closedLabel}</span>}
                    <button
                        type="button"
                        onClick={() => setIsExpanded((value) => !value)}
                        aria-expanded={isExpanded}
                        className="rounded-full p-1.5 text-[#414141] transition-colors hover:bg-[#F2F2F2] hover:text-[#2A2A2A]"
                        title={isExpanded ? 'Hide details' : 'Show details'}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                            <path d="m6 9 6 6 6-6" />
                        </svg>
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="mt-3 space-y-1.5 border-t border-[#d9d9d9] pt-3 text-xs font-semibold text-[#414141]">
                    {originalIssue && originalIssue !== ticket._displayTitle && (
                        <p><span className="font-black text-[#535353]">Originally reported: </span>{originalIssue}</p>
                    )}
                    <p><span className="font-black text-[#535353]">Resolution: </span>{subheader}</p>
                    <p className="flex flex-wrap gap-x-4 gap-y-1 text-[#414141]">
                        {openedLabel && <span>Opened {openedLabel}</span>}
                        {closedLabel && <span>Closed {closedLabel}</span>}
                        {pathLabel && <span>{pathLabel} repair</span>}
                    </p>
                    {attachments.length > 0 && (
                        <div className="pt-1">
                            <p className="text-[11px] font-black uppercase tracking-[0.1em] text-[#535353]">Evidence</p>
                            <div className="mt-1.5 flex flex-wrap gap-2">
                                {attachments.map((a, i) => {
                                    const href = a?.url || a?.storage_url || '';
                                    const name = a?.name || a?.original_filename || a?.caption || `Attachment ${i + 1}`;
                                    const isImage = (a?.media_kind === 'photo')
                                        || (a?.type === 'photo')
                                        || String(a?.mime_type || '').startsWith('image/');
                                    const isVideo = (a?.media_kind === 'video')
                                        || (a?.type === 'video')
                                        || String(a?.mime_type || '').startsWith('video/');
                                    if (isImage && href) {
                                        return (
                                            <a
                                                key={a?.attachment_id || i}
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={a?.caption || name}
                                                className="group relative block h-16 w-16 overflow-hidden rounded-lg border border-[#d9d9d9] bg-[#F9F9F9]"
                                            >
                                                <img src={href} alt={name} className="h-full w-full object-cover transition group-hover:opacity-90" loading="lazy" />
                                            </a>
                                        );
                                    }
                                    return (
                                        <a
                                            key={a?.attachment_id || i}
                                            href={href || undefined}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={a?.caption || name}
                                            className="flex h-16 w-16 flex-col items-center justify-center gap-1 rounded-lg border border-[#d9d9d9] bg-[#F9F9F9] px-1 text-center text-[#414141] transition hover:border-[#414141]"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                {isVideo
                                                    ? <><rect x="2" y="5" width="14" height="14" rx="2" /><path d="m22 8-6 4 6 4V8Z" /></>
                                                    : <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>}
                                            </svg>
                                            <span className="w-full truncate text-[9px] font-bold">{isVideo ? 'Video' : 'File'}</span>
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-4">
                        <button
                            type="button"
                            onClick={() => onOpen(ticket)}
                            className="inline-flex items-center gap-1.5 text-xs font-black text-[#D81F26] hover:underline"
                        >
                            Open conversation
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                        </button>
                        {onAddServiceRecord && (
                            <button
                                type="button"
                                onClick={() => onAddServiceRecord(ticket)}
                                className="inline-flex items-center gap-1.5 text-xs font-black text-[#D81F26] hover:underline"
                            >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                                Add to service history
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Significant keywords from a service title, for finding genuinely related records (same issue),
// not just any other record on the vehicle. Drops filler words and short tokens.
const SERVICE_TITLE_STOPWORDS = new Set([
    'the', 'a', 'an', 'and', 'or', 'of', 'to', 'in', 'for', 'my', 'is', 'are', 'was', 'were',
    'when', 'from', 'at', 'on', 'not', 'no', 'does', 'do', 'did', 'has', 'have', 'with', 'that',
    'this', 'it', 'its', 'as', 'by', 'be', 'now', 'below', 'above', 'completed', 'reported',
    'resolved', 'issue', 'service', 'chat', 'flow', 'diy', 'check', 'engine', 'light',
]);
const getServiceTitleKeywords = (title) => new Set(
    String(title || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, ' ')
        .replace(/-/g, ' ')
        .split(/\s+/)
        .map((w) => w.trim())
        .filter((w) => w.length > 2 && !SERVICE_TITLE_STOPWORDS.has(w))
);

// Pull a list of strings from a details field that might be an array, a string, or missing.
const asDetailList = (value) => {
    if (Array.isArray(value)) return value.map((v) => (typeof v === 'string' ? v : (v?.label || v?.title || v?.name || v?.description || ''))).map((s) => String(s).trim()).filter(Boolean);
    if (typeof value === 'string' && value.trim()) return [value.trim()];
    return [];
};

const formatInspectionDetail = (item) => {
    if (!item || typeof item !== 'object') return '';
    const head = item.area || item.system || item.name || 'Inspection';
    const details = [item.result, item.measurement, item.status, item.notes]
        .map((part) => String(part || '').trim())
        .filter(Boolean);
    return details.length ? `${head}: ${details.join(' / ')}` : String(head || '').trim();
};

const ServiceHistoryCard = ({ entry, allEntries = [], highlight = false, isNew = false, distanceUnit = 'mi', phoneNumber = '' }) => {
    const [isExpanded, setIsExpanded] = useState(highlight);
    const cardRef = useRef(null);
    useEffect(() => {
        if (highlight) {
            setIsExpanded(true);
            cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [highlight]);
    // A just-saved record: bring it into view if it landed off-screen (no-op when visible).
    useEffect(() => {
        if (isNew) cardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    }, [isNew]);
    const title = entry?.title || entry?.log_title || 'Service record';
    const performedLabel = formatServiceDate(entry?.performed_at);
    const relativeLabel = formatTimeAgo(entry?.performed_at);
    // Stored value is miles; show it in the user's preferred unit (never just relabel).
    const mileageLabel = entry?.odometer_mileage
        ? formatMileage(entry.odometer_mileage, distanceUnit)
        : '';
    const costLabel = formatServiceCost(entry?.invoice_total);
    const providerLabel = entry?.shop_name || formatProviderLabel(entry?.performed_by);
    const vehicleLabel = getServiceVehicleLabel(entry);
    const summary = [vehicleLabel, performedLabel, mileageLabel].filter(Boolean).join(' / ');
    const notes = (entry?.notes || '').trim();
    const vinLabel = entry?.vehicle_vin ? `VIN ${entry.vehicle_vin}` : '';
    const recordLabel = entry?.service_event_id ? `#${entry.service_event_id}` : '';

    // Richer detail sections, all optional — rendered only when the record actually carries them.
    const details = entry?.details && typeof entry.details === 'object' ? entry.details : {};
    const parts = Array.isArray(entry?.parts) ? entry.parts.filter((p) => p && (p.part_name || p.part_number)) : [];
    const symptoms = asDetailList(details.symptoms || details.observedSymptoms || entry?.symptoms);
    const workPerformed = asDetailList(details.workPerformed || details.work_performed || details.summary || details.likelyCauses || entry?.work_performed);
    const recommended = asDetailList(details.recommendedMaintenance || details.recommended || details.recommendedChecks || entry?.recommended_maintenance);
    const confirmedDiagnosis = asDetailList(details.confirmedDiagnosis);
    const rootCause = asDetailList(details.rootCause);
    const inspectionResults = Array.isArray(details.inspectionResults)
        ? details.inspectionResults.map(formatInspectionDetail).filter(Boolean)
        : asDetailList(details.inspectionResults);
    const monitoringItems = asDetailList(details.monitoringItems);
    const estimateDeviations = asDetailList(details.estimateDeviations);
    const ownerComments = asDetailList(details.ownerComments);
    const attachments = Array.isArray(entry?.attachments) ? entry.attachments.filter(Boolean) : [];
    const contextNotes = (details.notes && String(details.notes).trim() && String(details.notes).trim() !== notes) ? String(details.notes).trim() : '';
    // Related records: other saved services on the SAME vehicle that address the SAME issue,
    // matched by overlapping title keywords (so "Rough running…" relates to other rough-running
    // records, not to unrelated "Side mirrors changed"). Hidden when nothing genuinely relates.
    const ownKeywords = getServiceTitleKeywords(title);
    const related = (Array.isArray(allEntries) ? allEntries : [])
        .filter((e) => e && String(e.service_event_id) !== String(entry?.service_event_id)
            && String(e.vehicle_id || '') === String(entry?.vehicle_id || ''))
        .map((e) => {
            const kw = getServiceTitleKeywords(e.title || e.log_title);
            let overlap = 0;
            kw.forEach((w) => { if (ownKeywords.has(w)) overlap += 1; });
            return { entry: e, overlap };
        })
        .filter((r) => r.overlap >= 2 || (r.overlap >= 1 && ownKeywords.size <= 2))
        .sort((a, b) => b.overlap - a.overlap)
        .slice(0, 4)
        .map((r) => r.entry);

    return (
        <div ref={cardRef} className={`rounded-2xl border bg-white px-4 py-3.5 shadow-sm transition md:px-5 ${highlight ? 'border-[#D81F26] ring-2 ring-[#dbdbdb]' : 'border-[#d9d9d9] hover:border-[#B3B3B3]'} ${isNew ? 'animate-dash-new-glow' : ''}`}>
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] text-[#414141] transition-colors group-hover:border-[#B3B3B3] group-hover:bg-[#f5f5f5]">
                        <WrenchIcon />
                    </div>
                    <div className="min-w-0">
                        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                            <h3 className="min-w-0 max-w-full truncate text-sm font-black text-black">{title}</h3>
                            <Badge className={entry?.is_voided ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}>
                                {entry?.is_voided ? 'Voided' : 'Service Complete'}
                            </Badge>
                            {providerLabel && <Badge className="bg-[#F2F2F2] text-[#414141]">{providerLabel}</Badge>}
                            {Array.isArray(entry?.amendments) && entry.amendments.some((a) => a?.amendment_type === 'correction') && (
                                <Badge className="bg-amber-50 text-amber-700">Amended</Badge>
                            )}
                        </div>
                        <p className="mt-1 max-w-full truncate text-xs font-semibold text-[#414141]">
                            {summary || 'Service details saved'}
                        </p>
                        {(costLabel || vinLabel) && (
                            <p className="mt-1 max-w-full truncate text-xs font-bold text-[#414141]">
                                {[costLabel, vinLabel].filter(Boolean).join(' / ')}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-2 self-start">
                    {(performedLabel || relativeLabel) && <span className="text-xs font-bold text-[#414141]">{performedLabel || relativeLabel}</span>}
                    <button
                        type="button"
                        onClick={() => setIsExpanded((value) => !value)}
                        aria-expanded={isExpanded}
                        className="rounded-full p-1.5 text-[#414141] transition-colors hover:bg-[#F2F2F2] hover:text-[#2A2A2A]"
                        title={isExpanded ? 'Hide details' : 'Show details'}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                            <path d="m6 9 6 6 6-6" />
                        </svg>
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="mt-3 space-y-3 border-t border-[#d9d9d9] pt-3">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        <ServiceDetail label="Vehicle" value={vehicleLabel} />
                        <ServiceDetail label="Service Date" value={performedLabel} />
                        <ServiceDetail label="Mileage" value={mileageLabel} />
                        <ServiceDetail label="Provider" value={providerLabel} />
                        <ServiceDetail label="Cost" value={costLabel} />
                        <ServiceDetail label="Record" value={recordLabel} />
                    </div>
                    {vinLabel && (
                        <p className="text-xs font-semibold text-[#414141]">{vinLabel}</p>
                    )}

                    {symptoms.length > 0 && (
                        <ServiceSection label="Symptoms">
                            <ul className="list-disc space-y-0.5 pl-4">{symptoms.map((s, i) => <li key={i}>{s}</li>)}</ul>
                        </ServiceSection>
                    )}

                    {workPerformed.length > 0 && (
                        <ServiceSection label="Work performed">
                            <ul className="list-disc space-y-0.5 pl-4">{workPerformed.map((w, i) => <li key={i}>{w}</li>)}</ul>
                        </ServiceSection>
                    )}

                    {parts.length > 0 && (
                        <ServiceSection label="Parts used">
                            <div className="divide-y divide-[#f2f2f2]">
                                {parts.map((p, i) => (
                                    <div key={i} className="flex items-start justify-between gap-3 py-1.5">
                                        <span className="min-w-0 text-[#535353]">
                                            <span>
                                                {p.part_name || 'Part'}
                                                {p.part_number ? <span className="ml-1 font-bold text-[#414141]">({p.part_number})</span> : null}
                                                {p.quantity ? <span className="ml-1 text-[#414141]">×{p.quantity}</span> : null}
                                            </span>
                                            {p.notes ? <span className="mt-0.5 block text-xs font-semibold text-[#414141]">{p.notes}</span> : null}
                                        </span>
                                        <span className="shrink-0 font-bold text-[#414141]">{formatServiceCost(p.unit_cost) || ''}</span>
                                    </div>
                                ))}
                            </div>
                        </ServiceSection>
                    )}

                    {inspectionResults.length > 0 && (
                        <ServiceSection label="Inspection results">
                            <ul className="list-disc space-y-0.5 pl-4">{inspectionResults.map((r, i) => <li key={i}>{r}</li>)}</ul>
                        </ServiceSection>
                    )}

                    {confirmedDiagnosis.length > 0 && (
                        <ServiceSection label="Confirmed diagnosis">
                            <ul className="list-disc space-y-0.5 pl-4">{confirmedDiagnosis.map((d, i) => <li key={i}>{d}</li>)}</ul>
                        </ServiceSection>
                    )}

                    {rootCause.length > 0 && (
                        <ServiceSection label="Root cause">
                            <ul className="list-disc space-y-0.5 pl-4">{rootCause.map((r, i) => <li key={i}>{r}</li>)}</ul>
                        </ServiceSection>
                    )}

                    {(notes || contextNotes) && (
                        <ServiceSection label="Notes">
                            {notes && <p className="whitespace-pre-wrap break-words">{notes}</p>}
                            {contextNotes && <p className="mt-1 whitespace-pre-wrap break-words">{contextNotes}</p>}
                        </ServiceSection>
                    )}

                    {monitoringItems.length > 0 && (
                        <ServiceSection label="Items requiring monitoring">
                            <ul className="list-disc space-y-0.5 pl-4">{monitoringItems.map((m, i) => <li key={i}>{m}</li>)}</ul>
                        </ServiceSection>
                    )}

                    {estimateDeviations.length > 0 && (
                        <ServiceSection label="Estimate deviations">
                            <ul className="list-disc space-y-0.5 pl-4">{estimateDeviations.map((d, i) => <li key={i}>{d}</li>)}</ul>
                        </ServiceSection>
                    )}

                    {ownerComments.length > 0 && (
                        <ServiceSection label="Technician comments">
                            <ul className="list-disc space-y-0.5 pl-4">{ownerComments.map((c, i) => <li key={i}>{c}</li>)}</ul>
                        </ServiceSection>
                    )}

                    {attachments.length > 0 && (
                        <ServiceSection label="Attachments">
                            <ul className="space-y-1">
                                {attachments.map((a, i) => {
                                    const href = getServiceAttachmentUrl(a, phoneNumber);
                                    const name = a?.name || a?.original_filename || `Attachment ${i + 1}`;
                                    return (
                                        <li key={i}>
                                            {href
                                                ? <a href={href} target="_blank" rel="noopener noreferrer" className="font-black text-[#D81F26] hover:underline">{name}</a>
                                                : <span>{name}</span>}
                                        </li>
                                    );
                                })}
                            </ul>
                        </ServiceSection>
                    )}

                    {recommended.length > 0 && (
                        <ServiceSection label="Recommended maintenance">
                            <ul className="list-disc space-y-0.5 pl-4">{recommended.map((r, i) => <li key={i}>{r}</li>)}</ul>
                        </ServiceSection>
                    )}

                    {related.length > 0 && (
                        <ServiceSection label="Related records">
                            <ul className="space-y-1">
                                {related.map((r, i) => (
                                    <li key={r.service_event_id || i} className="flex items-center justify-between gap-3">
                                        <span className="min-w-0 truncate text-[#535353]">{r.title || r.log_title || 'Service record'}</span>
                                        <span className="shrink-0 text-[#414141]">{formatServiceDate(r.performed_at)}</span>
                                    </li>
                                ))}
                            </ul>
                        </ServiceSection>
                    )}
                </div>
            )}
        </div>
    );
};

const EmptyState = ({ title, body, action, icon }) => (
    <div className="rounded-2xl border border-dashed border-[#d9d9d9] bg-[#F9F9F9] px-6 py-10 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-[#d9d9d9] bg-white text-[#D81F26] shadow-sm">
            {icon || <DocumentIcon categoryId="other" />}
        </div>
        <h3 className="text-lg font-black text-black">{title}</h3>
        <p className="mx-auto mt-2 max-w-md text-sm font-semibold leading-relaxed text-[#414141]">{body}</p>
        {action}
    </div>
);

const ShowAllButton = ({ count, showAll, onToggle, label = 'Show all' }) => (
    <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-[#d9d9d9] bg-white py-3 text-sm font-black text-[#414141] transition-colors hover:bg-[#F9F9F9]"
    >
        {showAll ? 'Show less' : `${label} ${count}`}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className={showAll ? 'rotate-180' : ''}><path d="m6 9 6 6 6-6" /></svg>
    </button>
);

const GloveboxPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        authState,
        openAuthModal,
        onStartNewChat,
        onSelectThread,
        recentDiagnostics = [],
    } = useOutletContext();

    const [documents, setDocuments] = useState([]);
    const [serviceHistory, setServiceHistory] = useState([]);
    const [activeVehicle, setActiveVehicle] = useState(null);
    const [distanceUnit, setDistanceUnit] = useState('mi'); // user's preferred display unit
    const [vehiclesCount, setVehiclesCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [profileLoading, setProfileLoading] = useState(false);
    const [documentsError, setDocumentsError] = useState(null);
    const [historyError, setHistoryError] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState('history');
    const [documentsShowAll, setDocumentsShowAll] = useState(false);
    const [serviceHistoryShowAll, setServiceHistoryShowAll] = useState(false);
    const [documentPreview, setDocumentPreview] = useState(null);
    const documentInputRef = useRef(null);
    const uploadTargetRef = useRef(null);
    const [documentUploading, setDocumentUploading] = useState(false);
    const [uploadFeedback, setUploadFeedback] = useState(null);
    // A specific service record to open/highlight (arriving from a chat service card).
    const [highlightServiceEventId, setHighlightServiceEventId] = useState(null);
    // A specific document to open once the list loads (arriving from a notification).
    const [pendingOpenDocId, setPendingOpenDocId] = useState(null);
    // "Add service record" form (log additional info straight into Service History).
    const [serviceFormOpen, setServiceFormOpen] = useState(false);
    const [serviceForm, setServiceForm] = useState({ title: '', notes: '', odometer: '', date: '', ticketId: null });
    const [serviceFormSaving, setServiceFormSaving] = useState(false);
    const [serviceFormError, setServiceFormError] = useState('');
    // Soft odometer/timeline warnings returned by the server (O-5/O-6). When present the
    // driver must choose "Save anyway" (re-submit confirmed) or "Fix the mileage".
    const [serviceWarnings, setServiceWarnings] = useState([]);
    // Direction the tab content slides in from after a user tab switch ({ tab, className }).
    // Null on first load and for deep-link switches, so those render without the slide.
    const [tabSlide, setTabSlide] = useState(null);
    // A record the user just added/uploaded ({ kind: 'service' | 'document', id }): it glows
    // red once, then the state clears.
    const [newItem, setNewItem] = useState(null);
    const newItemTimerRef = useRef(null);

    const isAuthenticated = authState?.status === 'authenticated';
    const activeVehicleLabel = getVehicleLabel(activeVehicle);

    const fetchDocuments = useCallback(async () => {
        if (!authState.identity) return;
        setIsLoading(true);
        setDocumentsError(null);
        try {
            const response = await fetch(`${getUserDetailsBaseUrl()}/api/documents/me`, {
                credentials: 'include',
            });
            if (!response.ok) throw new Error('Failed to fetch documents');
            const data = await response.json();
            if (data.error) throw new Error(data.error);
            const list = Array.isArray(data.documents) ? data.documents : [];
            setDocuments(list);
            return list;
        } catch (err) {
            console.error('Glovebox Fetch Error:', err);
            setDocumentsError(err.message);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [authState.identity]);

    const fetchServiceHistory = useCallback(async (vehicleId) => {
        if (!authState.identity || !vehicleId) {
            setServiceHistory([]);
            return;
        }
        setHistoryLoading(true);
        setHistoryError(null);
        try {
            const response = await fetch(`${getUserDetailsBaseUrl()}/api/service-history/${encodeURIComponent(authState.identity)}?vehicle_id=${encodeURIComponent(vehicleId)}&limit=100`);
            if (!response.ok) throw new Error('Failed to fetch service history');
            const data = await response.json();
            if (data.error) throw new Error(data.error);
            const list = Array.isArray(data.entries) ? data.entries : [];
            setServiceHistory(list);
            return list;
        } catch (err) {
            console.error('Glovebox Service History Fetch Error:', err);
            setHistoryError(err.message);
            setServiceHistory([]);
            return null;
        } finally {
            setHistoryLoading(false);
        }
    }, [authState.identity]);

    // Same endpoint GaragePage/VehicleSelector already use for the single global
    // active vehicle — Glovebox scopes to it instead of its own vehicle dropdown.
    const fetchActiveVehicle = useCallback(async () => {
        if (!authState.identity) return;
        setProfileLoading(true);
        try {
            const response = await fetch(`${getUserDetailsBaseUrl()}/api/lookup_user_data?phone_number=${encodeURIComponent(authState.identity)}&query=vehicle_worklist`);
            const data = await response.json().catch(() => ({}));
            const vehicles = Array.isArray(data?.result?.vehicles) ? data.result.vehicles : [];
            setVehiclesCount(vehicles.length);
            setActiveVehicle(data?.result?.active_vehicle || vehicles[0] || null);
            // #409: CarFido communicates only in imperial — always display miles.
            setDistanceUnit('mi');
        } catch (err) {
            console.warn('Glovebox vehicle profile skipped:', err);
            setActiveVehicle(null);
            setVehiclesCount(0);
        } finally {
            setProfileLoading(false);
        }
    }, [authState.identity]);

    useEffect(() => {
        if (isAuthenticated && authState.identity) {
            fetchDocuments();
            fetchActiveVehicle();
        }
    }, [isAuthenticated, authState.identity, fetchDocuments, fetchActiveVehicle]);

    useEffect(() => {
        if (isAuthenticated && authState.identity && activeVehicle?.vehicle_id) {
            fetchServiceHistory(activeVehicle.vehicle_id);
        } else {
            setServiceHistory([]);
        }
    }, [isAuthenticated, authState.identity, activeVehicle?.vehicle_id, fetchServiceHistory]);

    // Mirrors VehicleSelector.jsx's handleSelect — switches the single global
    // active vehicle rather than a page-local filter.
    const handleSwitchVehicle = useCallback(async (vehicleId) => {
        if (!authState.identity || !vehicleId) return;
        try {
            await fetch(`${getUserDetailsBaseUrl()}/api/vehicle_selection`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone_number: authState.identity, vehicle_id: vehicleId }),
            });
            window.dispatchEvent(new CustomEvent('dash_vehicle_switched'));
        } catch (err) {
            console.error('Glovebox vehicle switch failed:', err);
        }
    }, [authState.identity]);

    useEffect(() => {
        const vehicleId = location.state?.vehicleId;
        const categoryId = location.state?.categoryId;
        const tab = location.state?.tab;
        const openServiceEventId = location.state?.openServiceEventId;
        const openDocId = location.state?.openDocId;
        if (vehicleId) handleSwitchVehicle(vehicleId);
        if (categoryId) setCategoryFilter(String(categoryId));
        // Deep-link tab changes land in place, without the user-switch slide.
        if (tab || openServiceEventId != null || openDocId != null) setTabSlide(null);
        if (tab === 'history' || tab === 'documents') setActiveTab(tab);
        // Opened from a chat service card: land on History and reveal the exact record.
        if (openServiceEventId != null) {
            setActiveTab('history');
            setServiceHistoryShowAll(true);
            setHighlightServiceEventId(String(openServiceEventId));
        }
        // Opened from a notification: land on Documents and open that exact document.
        if (openDocId != null) {
            setActiveTab('documents');
            setDocumentsShowAll(true);
            setPendingOpenDocId(String(openDocId));
        }
    }, [location.state, handleSwitchVehicle]);

    // User tab switch: History sits left of Documents, so the new view slides in from
    // the side it lives on.
    const handleTabChange = useCallback((nextTab) => {
        if (nextTab === activeTab) return;
        const order = ['history', 'documents'];
        const className = order.indexOf(nextTab) > order.indexOf(activeTab)
            ? 'animate-dash-slide-in-right'
            : 'animate-dash-slide-in-left';
        setTabSlide({ tab: nextTab, className });
        setActiveTab(nextTab);
    }, [activeTab]);

    // Glow a just-added record once (the CSS halo runs 1.6s), then clear it.
    const flashNewItem = useCallback((kind, id) => {
        if (id == null || id === '') return;
        if (newItemTimerRef.current) clearTimeout(newItemTimerRef.current);
        setNewItem({ kind, id: String(id) });
        newItemTimerRef.current = setTimeout(() => {
            newItemTimerRef.current = null;
            setNewItem(null);
        }, 1700);
    }, []);

    useEffect(() => () => {
        if (newItemTimerRef.current) clearTimeout(newItemTimerRef.current);
    }, []);

    // Open the "Add service record" form. `prefill` seeds the title/notes when the record
    // is being added from a closed diagnostic. Mileage stays blank unless the driver knows it.
    const openServiceRecordForm = useCallback((prefill = {}) => {
        const todayISO = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, local
        setServiceForm({
            title: prefill.title || '',
            notes: prefill.notes || '',
            odometer: '',
            date: todayISO,
            ticketId: prefill.ticketId || null,
        });
        setServiceFormError('');
        setServiceWarnings([]);
        setServiceFormOpen(true);
    }, []);

    const handleSaveServiceRecord = useCallback(async (confirm = false) => {
        const title = serviceForm.title.trim();
        if (!title) {
            setServiceFormError('Please enter what service was done.');
            return;
        }
        if (!authState.identity) return;
        // Client-side guards (the server enforces these too): whole-number odometer + not future.
        const rawOdo = String(serviceForm.odometer).trim();
        if (rawOdo && !/^\d[\d,\s]*$/.test(rawOdo)) {
            setServiceFormError('Enter the odometer as a whole number.');
            return;
        }
        // Enforce the 1,000,000-mile maximum (#412) on the canonical (miles) value.
        const odoMilesCheck = rawOdo ? toMiles(rawOdo, distanceUnit) : null;
        if (odoMilesCheck !== null && odoMilesCheck > MAX_ODOMETER_MILES) {
            setServiceFormError(ODOMETER_TOO_HIGH_MESSAGE);
            return;
        }
        const todayISO = new Date().toLocaleDateString('en-CA');
        if (!serviceForm.date) {
            setServiceFormError('Service date is required.');
            return;
        }
        if (serviceForm.date && serviceForm.date > todayISO) {
            setServiceFormError('Service history records work already done. Pick today or earlier.');
            return;
        }
        setServiceFormSaving(true);
        setServiceFormError('');
        const odoDigits = rawOdo.replace(/[^\d]/g, '');
        try {
            const resp = await fetch(`${getUserDetailsBaseUrl()}/api/service-history/${encodeURIComponent(authState.identity)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    vehicle_id: activeVehicle?.vehicle_id || undefined,
                    title,
                    notes: serviceForm.notes.trim() || undefined,
                    odometer: odoDigits ? Number(odoDigits) : undefined,
                    // The value is entered in the user's preferred unit; tell the server so
                    // it converts km→miles before storing (never stores km as miles).
                    odometer_unit: distanceUnit,
                    performed_at: serviceForm.date || undefined,
                    performed_by: 'owner',
                    diagnostic_ticket_id: serviceForm.ticketId || undefined,
                    confirm_warnings: confirm || undefined,
                }),
            });
            const data = await resp.json().catch(() => ({}));
            // Soft warning (O-5 timeline / O-6 unrealistic): show it and let the driver decide.
            if (data?.status === 'confirm_required') {
                setServiceWarnings(Array.isArray(data.warnings) ? data.warnings : []);
                setServiceFormSaving(false);
                return;
            }
            if (!resp.ok || data?.status === 'error') {
                throw new Error(data?.detail || data?.message || 'Unable to save service record.');
            }
            setServiceWarnings([]);
            setServiceFormOpen(false);
            const beforeIds = new Set(serviceHistory.map((e) => String(e?.service_event_id)));
            const refreshed = activeVehicle?.vehicle_id ? await fetchServiceHistory(activeVehicle.vehicle_id) : null;
            // The new record's id: from the response when the server sends it, otherwise
            // whichever entry wasn't in the list before the refresh.
            let newId = data?.service_event_id ?? data?.entry?.service_event_id ?? null;
            if (newId == null && Array.isArray(refreshed)) {
                const added = refreshed.find((e) => e?.service_event_id != null && !beforeIds.has(String(e.service_event_id)));
                newId = added ? added.service_event_id : null;
            }
            if (newId != null && Array.isArray(refreshed)) {
                const index = refreshed.findIndex((e) => String(e?.service_event_id) === String(newId));
                if (index >= HISTORY_PREVIEW_COUNT) setServiceHistoryShowAll(true);
            }
            flashNewItem('service', newId);
            // Current mileage may have risen — refresh the vehicle everywhere.
            if (data?.mileage_updated) window.dispatchEvent(new CustomEvent('dash_vehicle_updated'));
        } catch (err) {
            setServiceFormError(err?.message || 'Unable to save service record.');
        } finally {
            setServiceFormSaving(false);
        }
    }, [serviceForm, authState.identity, activeVehicle, fetchServiceHistory, distanceUnit, serviceHistory, flashNewItem]);

    useEffect(() => {
        if (!isAuthenticated) return undefined;
        const refresh = () => {
            fetchDocuments();
            fetchActiveVehicle();
        };
        window.addEventListener('dash_vehicle_updated', refresh);
        window.addEventListener('dash_vehicle_switched', refresh);
        return () => {
            window.removeEventListener('dash_vehicle_updated', refresh);
            window.removeEventListener('dash_vehicle_switched', refresh);
        };
    }, [isAuthenticated, fetchDocuments, fetchActiveVehicle]);

    useEffect(() => {
        setDocumentsShowAll(false);
    }, [categoryFilter, search]);

    // Scope strictly to the active vehicle. Unassigned/account-level documents are
    // not vehicle records and must not appear under every vehicle — except the pilot
    // reward, which belongs to the account and shows with or without a vehicle.
    const enrichedDocuments = useMemo(() => {
        const activeVehicleId = activeVehicle?.vehicle_id ? String(activeVehicle.vehicle_id) : '';
        return documents
            .filter((doc) => {
                if (doc?.document_kind === 'pilot_reward') return true;
                const vehicleId = getDocumentVehicleId(doc);
                return Boolean(activeVehicleId) && Boolean(vehicleId) && String(vehicleId) === activeVehicleId;
            })
            .map((doc) => ({
                doc,
                category: inferDocumentCategory(doc),
                status: getDocumentStatus(doc),
                searchText: getDocumentSearchText(doc),
                timestamp: getDocumentTimestamp(doc),
            }));
    }, [documents, activeVehicle]);

    const filteredDocuments = useMemo(() => {
        const query = search.trim().toLowerCase();
        return enrichedDocuments
            .filter((item) => {
                const matchesSearch = !query || item.searchText.includes(query);
                const matchesCategory = categoryFilter === 'all' || item.category.id === categoryFilter;
                return matchesSearch && matchesCategory;
            })
            .sort((a, b) => b.timestamp - a.timestamp);
    }, [categoryFilter, enrichedDocuments, search]);

    const manualDocs = useMemo(
        () => enrichedDocuments
            .filter((item) => item?.category?.id === 'manual')
            .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)),
        [enrichedDocuments],
    );
    const manualLinks = useMemo(() => buildPublicManualLinks(activeVehicle), [activeVehicle]);
    const quickDocumentCards = useMemo(() => {
        const registration = getLatestDocumentForCategory(enrichedDocuments, 'registration');
        const insurance = getLatestDocumentForCategory(enrichedDocuments, 'insurance');
        const manual = getLatestDocumentForCategory(enrichedDocuments, 'manual');
        const registrationStatus = registration?.status;
        const insuranceStatus = insurance?.status;
        const manualCount = manualDocs.length + manualLinks.length;
        return [
            {
                id: 'registration',
                label: 'Registration',
                doc: registration?.doc,
                status: registrationStatus,
                needsUpload: !registration || ['expired', 'expiring'].includes(registrationStatus?.id),
                summary: registration
                    ? `${getDocumentTitle(registration.doc)} / ${getDocumentDateLabel(registration.doc)}`
                    : `No registration document for ${activeVehicleLabel || 'this vehicle'}.`,
            },
            {
                id: 'insurance',
                label: 'Insurance',
                doc: insurance?.doc,
                status: insuranceStatus,
                needsUpload: !insurance || ['expired', 'expiring'].includes(insuranceStatus?.id),
                summary: insurance
                    ? `${getDocumentTitle(insurance.doc)} / ${getDocumentDateLabel(insurance.doc)}`
                    : `No insurance card for ${activeVehicleLabel || 'this vehicle'}.`,
            },
            {
                id: 'manual',
                label: 'Manuals',
                doc: manual?.doc,
                status: manual ? manual.status : null,
                needsUpload: false,
                summary: manualCount > 0
                    ? `${manualCount} manual ${manualCount === 1 ? 'reference' : 'references'} available.`
                    : `No manual references for ${activeVehicleLabel || 'this vehicle'} yet.`,
            },
        ];
    }, [activeVehicleLabel, enrichedDocuments, manualDocs, manualLinks]);

    const visibleDocuments = documentsShowAll ? filteredDocuments : filteredDocuments.slice(0, DOCUMENTS_PREVIEW_COUNT);
    const hasActiveFilters = Boolean(search.trim()) || categoryFilter !== 'all';

    const historyTickets = useMemo(() => (
        (Array.isArray(recentDiagnostics) ? recentDiagnostics : [])
            .filter((ticket) => isTicketClosed(ticket))
            .filter((ticket) => {
                const vehicleId = activeVehicle?.vehicle_id || activeVehicle?.id;
                if (!vehicleId) return true;
                const ticketVehicleId = ticket?.vehicle_id || ticket?.vehicleId || ticket?.vehicle?.vehicle_id || ticket?.vehicle?.id;
                if (ticketVehicleId) return String(ticketVehicleId) === String(vehicleId);
                const ticketLabel = ticketVehicleLabel(ticket);
                return !ticketLabel || !activeVehicleLabel || ticketLabel === activeVehicleLabel;
            })
            .map((ticket) => ({
                ...ticket,
                _displayTitle: resolveTicketDisplayTitle(ticket),
            }))
    ), [recentDiagnostics, activeVehicle, activeVehicleLabel]);
    const hasHistoryContent = serviceHistory.length > 0 || historyTickets.length > 0 || historyLoading;
    // Completed diagnostic cases now live INSIDE Service History (the standalone "Closed
    // Diagnostics" section was removed). Merge resolved diagnostics with logged service records,
    // newest first, so the whole repair/service record for the vehicle reads as one timeline.
    const mergedHistory = useMemo(() => {
        const _ts = (value) => {
            const t = new Date(value || 0).getTime();
            return Number.isFinite(t) ? t : 0;
        };
        const serviceRows = (serviceHistory || []).map((entry, index) => ({
            kind: 'service',
            key: `svc-${entry.service_event_id || entry.repair_log_id || `${entry.title}-${index}`}`,
            date: _ts(entry.performed_at || entry.created_at),
            entry,
        }));
        const diagnosticRows = (historyTickets || []).map((ticket) => ({
            kind: 'diagnostic',
            key: `diag-${ticket.diagnostic_ticket_id || ticketThreadId(ticket)}`,
            date: _ts(ticket.resolved_at || ticket.closed_at || ticket.updated_at || ticket.created_at),
            ticket,
        }));
        return [...serviceRows, ...diagnosticRows].sort((a, b) => b.date - a.date);
    }, [serviceHistory, historyTickets]);
    const visibleMergedHistory = serviceHistoryShowAll ? mergedHistory : mergedHistory.slice(0, HISTORY_PREVIEW_COUNT);

    const clearFilters = () => {
        setSearch('');
        setCategoryFilter('all');
    };

    // Open a generated diagnostic-report document in-app (the report view), not as a file.
    const handleOpenReportDoc = useCallback((doc) => {
        const { reportId, threadId } = getDiagnosticReportRef(doc);
        const id = reportId || (threadId ? `thread-${threadId}-diagnostic-report` : '');
        if (!id) return;
        const seed = {
            id,
            threadId: threadId || undefined,
            diagnosticTicketId: doc?.diagnostic_ticket_id || undefined,
            vehicle: activeVehicleLabel || undefined,
        };
        navigate(`/diagnostic-report/${encodeURIComponent(id)}`, { state: { report: seed } });
    }, [navigate, activeVehicleLabel]);

    // A notification deep-link named a specific document — once the list has loaded,
    // open that exact document: a diagnostic report opens in the report view, an image
    // in the preview modal. (Other file types stay revealed in the Documents list.)
    useEffect(() => {
        if (!pendingOpenDocId || !Array.isArray(documents) || documents.length === 0) return;
        const doc = documents.find((d) => String(d.doc_id ?? d.docId ?? '') === String(pendingOpenDocId));
        if (!doc) return;
        setPendingOpenDocId(null);
        if (isDiagnosticReportDoc(doc)) {
            handleOpenReportDoc(doc);
        } else if (isImageDocument(doc)) {
            setDocumentPreview(doc);
        }
    }, [pendingOpenDocId, documents, handleOpenReportDoc]);

    const handleUploadDocument = (categoryId) => {
        if (documentUploading || !activeVehicle?.vehicle_id) return;
        uploadTargetRef.current = { categoryId, vehicleId: activeVehicle.vehicle_id };
        setUploadFeedback(null);
        documentInputRef.current?.click();
    };

    const handleDocumentSelected = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        const target = uploadTargetRef.current;
        if (!file || !target || documentUploading) return;
        if (file.size > 50 * 1024 * 1024) {
            setUploadFeedback({ error: true, message: 'Please choose a file smaller than 50 MB.' });
            return;
        }
        setDocumentUploading(true);
        setUploadFeedback(null);
        try {
            const body = new FormData();
            body.append('file', file);
            body.append('vehicle_id', target.vehicleId);
            body.append('document_kind', target.categoryId);
            const response = await fetch(`${getUserDetailsBaseUrl()}/api/documents/me/upload`, {
                method: 'POST', credentials: 'include', body,
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || data.error || data.status !== 'success') {
                throw new Error(typeof data.detail === 'string' ? data.detail : data.error || 'Could not upload the document. Please try again.');
            }
            setUploadFeedback({ message: data.duplicate ? 'This document is already in your Glove Box.' : 'Document uploaded successfully.' });
            setCategoryFilter('all');
            setSearch('');
            const beforeIds = new Set(documents.map(getDocId));
            const refreshed = await fetchDocuments();
            // Glow the uploaded document: id from the response if present, otherwise the
            // doc that wasn't in the list before (a duplicate upload adds nothing new).
            let newId = data?.doc_id ?? data?.document?.doc_id ?? null;
            if (newId == null && Array.isArray(refreshed)) {
                const added = refreshed.find((d) => getDocId(d) && !beforeIds.has(getDocId(d)));
                newId = added ? getDocId(added) : null;
            }
            if (newId != null && Array.isArray(refreshed)) {
                const sameVehicle = refreshed
                    .filter((d) => String(getDocumentVehicleId(d) || '') === String(target.vehicleId))
                    .sort((a, b) => getDocumentTimestamp(b) - getDocumentTimestamp(a));
                const index = sameVehicle.findIndex((d) => getDocId(d) === String(newId));
                if (index >= DOCUMENTS_PREVIEW_COUNT) setDocumentsShowAll(true);
            }
            flashNewItem('document', newId);
        } catch (error) {
            setUploadFeedback({ error: true, message: error.message || 'Could not upload the document. Please try again.' });
        } finally {
            setDocumentUploading(false);
        }
    };

    const handleOpenQuickDocument = useCallback((doc) => {
        if (!doc) return;
        if (isDiagnosticReportDoc(doc)) {
            handleOpenReportDoc(doc);
            return;
        }
        const href = getDocumentFileUrl(doc);
        if (isImageDocument(doc)) {
            setDocumentPreview({ ...doc, url: href || doc.url });
            return;
        }
        if (href && typeof window !== 'undefined') {
            window.open(href, '_blank', 'noopener,noreferrer');
        }
    }, [handleOpenReportDoc]);

    const handleOpenTicket = useCallback((ticket) => {
        const threadId = ticketThreadId(ticket);
        if (threadId && onSelectThread) {
            onSelectThread(threadId);
            return;
        }
        const id = ticketRealId(ticket) || threadId || ticket?.id;
        if (id) {
            navigate(`/diagnostic-report/${encodeURIComponent(id)}`, { state: { report: ticket } });
        }
    }, [navigate, onSelectThread]);

    const handleAddServiceRecordForTicket = useCallback((ticket) => {
        openServiceRecordForm({
            title: ticketTitle(ticket) || 'Completed diagnostic repair',
            notes: ticket?.resolution_summary || ticket?.summary || ticket?.customerComplaint || ticket?.issue || '',
            ticketId: ticketRealId(ticket) || ticketThreadId(ticket) || null,
        });
    }, [openServiceRecordForm]);

    const handleShowAllVehicles = () => {
        navigate('/garage');
    };

    const handleAddVehicle = () => {
        if (onStartNewChat) onStartNewChat();
        navigate('/chat', { state: { openVehicleForm: true } });
    };

    const visibleError = activeTab === 'history' ? historyError : documentsError;

    if (authState?.status === 'loading') {
        return (
            <div className="h-full w-full p-8 flex flex-col items-center justify-center bg-white">
                <div className="w-12 h-12 border-4 border-black/5 border-t-black rounded-full animate-spin"></div>
                <p className="mt-4 text-xs font-black uppercase tracking-widest text-black/30">Authenticating...</p>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="h-full w-full flex flex-col items-center justify-center p-8 text-center bg-white overflow-y-auto">
                <div className="max-w-md w-full animate-in fade-in slide-in-from-bottom-8 duration-700">
                    <div className="w-24 h-24 bg-[#eaeaea] rounded-[32px] flex items-center justify-center mx-auto mb-8 shadow-inner">
                        <DocumentIcon categoryId="other" />
                    </div>
                    <h2 className="text-3xl font-black text-black tracking-tight mb-4">Digital Glove Box</h2>
                    <p className="text-black/50 font-semibold leading-relaxed mb-10">
                        Securely store and access your vehicle registration, insurance, manuals, uploads, and CarFido reports in one place.
                    </p>
                    <button
                        onClick={openAuthModal}
                        className="w-full py-4 bg-black text-white rounded-full font-black uppercase tracking-[0.2em] text-xs shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all"
                    >
                        Login
                    </button>
                </div>
            </div>
        );
    }

    if ((isLoading || profileLoading) && documents.length === 0 && !activeVehicle) {
        return (
            <div className="h-full w-full p-8 flex flex-col items-center justify-center bg-white">
                <div className="w-12 h-12 border-4 border-black/5 border-t-black rounded-full animate-spin"></div>
                <p className="mt-4 text-xs font-black uppercase tracking-widest text-black/30">Opening Glove Box...</p>
            </div>
        );
    }

    return (
        // overflow-x-hidden: the tab slide starts 28px off to the side; without it phones
        // get a brief horizontal scrollbar. Modals are position:fixed outside the slide
        // wrapper, and nothing in the page pops out sideways, so nothing gets clipped.
        <div className="h-full w-full overflow-y-auto overflow-x-hidden bg-[#F9F9F9] custom-scrollbar relative">
            <div className="max-w-5xl mx-auto px-4 pt-8 pb-10 md:px-6 md:pt-10">
                <header className="mb-6 animate-in fade-in slide-in-from-top-4 duration-700">
                    <h1 className="text-4xl md:text-5xl font-black text-black tracking-tighter">Glove Box</h1>
                    <p className="mt-2 max-w-2xl text-sm font-semibold leading-relaxed text-[#414141]">
                        Vehicle documents &amp; service history{activeVehicleLabel ? ` · ${activeVehicleLabel}` : ''}
                    </p>
                </header>

                {visibleError && (
                    <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 font-bold mb-6 flex items-center gap-3 text-sm">
                        <span>!</span> {visibleError}
                    </div>
                )}

                {!profileLoading && vehiclesCount === 0 ? (
                    <>
                    {/* The pilot reward belongs to the account, so it shows even before a vehicle is added. */}
                    {documents.some((doc) => doc?.document_kind === 'pilot_reward') && (
                        <section className="mb-6 overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-sm">
                            <div className="divide-y divide-[#d9d9d9]">
                                {documents
                                    .filter((doc) => doc?.document_kind === 'pilot_reward')
                                    .map((doc) => <RewardListItem key={doc.doc_id || doc.name} doc={doc} />)}
                            </div>
                        </section>
                    )}
                    <EmptyState
                        title="Add a vehicle to get started"
                        body="Once you add a vehicle, its documents and service history will show up here."
                        action={(
                            <button
                                type="button"
                                onClick={handleAddVehicle}
                                className="mt-5 inline-flex min-h-[42px] items-center justify-center rounded-full bg-black px-5 text-sm font-black text-white transition hover:bg-[#303030]"
                            >
                                Add a Vehicle
                            </button>
                        )}
                    />
                    </>
                ) : (
                    <>
                        <div className="mb-8 mt-2 flex justify-center">
                            <GloveboxTabs active={activeTab} onChange={handleTabChange} />
                        </div>

                        {/* Keyed by tab so the slide replays on every user switch. */}
                        <div key={activeTab} className={tabSlide && tabSlide.tab === activeTab ? tabSlide.className : undefined}>
                        {activeTab === 'history' ? (
                            !hasHistoryContent ? (
                                <EmptyState
                                    icon={<HistoryIcon />}
                                    title="No service history for this vehicle"
                                    body={`Completed service records for ${activeVehicleLabel || 'this vehicle'} will appear here.`}
                                    action={(
                                        <div className="mt-5 flex flex-col items-center gap-3">
                                            <button
                                                type="button"
                                                onClick={() => openServiceRecordForm()}
                                                className="inline-flex min-h-[42px] items-center justify-center gap-1.5 rounded-full bg-[#D81F26] px-5 text-sm font-black text-white transition hover:bg-[#B8181E]"
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                                                Add service record
                                            </button>
                                            {vehiclesCount > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={handleShowAllVehicles}
                                                    className="text-sm font-black text-[#D81F26] underline underline-offset-2"
                                                >
                                                    Show all vehicles
                                                </button>
                                            )}
                                        </div>
                                    )}
                                />
                            ) : (
                                <section className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <div className="space-y-3">
                                        <div className="flex items-end justify-between gap-3">
                                            <div>
                                                <h2 className="flex items-center gap-2 text-sm font-black text-black"><span className="h-3.5 w-1 shrink-0 rounded-full bg-[#D81F26]" aria-hidden="true" />Service History</h2>
                                                <p className="mt-0.5 text-xs font-semibold text-[#6B6B6B]">Completed service records and resolved diagnostics for this vehicle.</p>
                                            </div>
                                            <div className="flex shrink-0 items-center gap-3">
                                                {mergedHistory.length > 0 && <span className="text-xs font-bold text-[#414141]">{mergedHistory.length} logged</span>}
                                                <button
                                                    type="button"
                                                    onClick={() => openServiceRecordForm()}
                                                    className="inline-flex min-h-[34px] items-center gap-1.5 rounded-full border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26]"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                                                    Add record
                                                </button>
                                            </div>
                                        </div>
                                        {historyLoading && (
                                            <p className="rounded-2xl border border-[#d9d9d9] bg-white px-4 py-3 text-xs font-black uppercase tracking-widest text-[#414141] md:px-5">Refreshing...</p>
                                        )}
                                        {!historyLoading && mergedHistory.length === 0 ? (
                                            <div className="rounded-2xl border border-dashed border-[#d9d9d9] bg-[#F9F9F9] px-4 py-5 text-sm font-bold text-[#414141]">
                                                No service records logged yet.
                                            </div>
                                        ) : (
                                            visibleMergedHistory.map((row) => (
                                                row.kind === 'diagnostic' ? (
                                                    <DiagnosticHistoryCard
                                                        key={row.key}
                                                        ticket={row.ticket}
                                                        onOpen={handleOpenTicket}
                                                        onAddServiceRecord={handleAddServiceRecordForTicket}
                                                    />
                                                ) : (
                                                    <ServiceHistoryCard
                                                        key={row.key}
                                                        entry={row.entry}
                                                        allEntries={serviceHistory}
                                                        distanceUnit={distanceUnit}
                                                        phoneNumber={authState.identity}
                                                        highlight={highlightServiceEventId != null && String(row.entry.service_event_id) === String(highlightServiceEventId)}
                                                        isNew={newItem?.kind === 'service' && String(row.entry.service_event_id) === newItem.id}
                                                    />
                                                )
                                            ))
                                        )}
                                        {mergedHistory.length > HISTORY_PREVIEW_COUNT && (
                                            <ShowAllButton
                                                count={mergedHistory.length}
                                                showAll={serviceHistoryShowAll}
                                                onToggle={() => setServiceHistoryShowAll((value) => !value)}
                                            />
                                        )}
                                    </div>
                                    {mergedHistory.length === 0 && (
                                        <div className="pt-1">
                                            <button
                                                type="button"
                                                onClick={() => navigate('/chat', { state: { q: 'I want to log a completed service record for my vehicle.' } })}
                                                className="inline-flex min-h-[38px] items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-4 text-xs font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26]"
                                            >
                                                Log Service via Chat
                                            </button>
                                        </div>
                                    )}
                                </section>
                            )
                        ) : (
                            <>
                                <div className="mb-4 flex flex-col gap-3">
                                    <input
                                        ref={documentInputRef}
                                        type="file"
                                        accept=".avif,.bmp,.csv,.doc,.docx,.gif,.heic,.heif,.jpeg,.jfif,.jpg,.pdf,.png,.tif,.tiff,.txt,.webp,.xls,.xlsx"
                                        aria-label="Upload vehicle document"
                                        className="hidden"
                                        onChange={handleDocumentSelected}
                                        disabled={documentUploading}
                                    />
                                    <DocumentQuickCards
                                        cards={quickDocumentCards}
                                        manualDocs={manualDocs}
                                        manualLinks={manualLinks}
                                        onOpenDocument={handleOpenQuickDocument}
                                        onUpload={handleUploadDocument}
                                        uploading={documentUploading}
                                        newDocId={newItem?.kind === 'document' ? newItem.id : null}
                                    />
                                    {documentUploading && <p role="status" className="text-sm font-semibold text-[#414141]">Uploading document...</p>}
                                    {uploadFeedback && (
                                        <p role={uploadFeedback.error ? 'alert' : 'status'} className={`text-sm font-semibold ${uploadFeedback.error ? 'text-red-600' : 'text-emerald-700'}`}>
                                            {uploadFeedback.message}
                                        </p>
                                    )}
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="flex min-h-[42px] flex-1 items-center gap-2 rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-[#414141] focus-within:border-[#D81F26] focus-within:bg-white">
                                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                                                <circle cx="11" cy="11" r="8" />
                                                <path d="m21 21-4.35-4.35" />
                                            </svg>
                                            <input
                                                type="search"
                                                value={search}
                                                onChange={(event) => setSearch(event.target.value)}
                                                placeholder="Search documents..."
                                                className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-black placeholder:text-[#414141] outline-none"
                                            />
                                        </span>
                                    </div>

                                    <CategoryPills value={categoryFilter} onChange={setCategoryFilter} />
                                </div>

                                {enrichedDocuments.length === 0 ? (
                                    <EmptyState
                                        title={`No documents for ${activeVehicleLabel || 'this vehicle'}`}
                                        body="Use the Upload new shortcuts above to add your registration, insurance, or manual."
                                    />
                                ) : filteredDocuments.length === 0 ? (
                                    <EmptyState
                                        title="No matching documents"
                                        body="Clear a filter or search for another file name or category."
                                        action={(
                                            <button
                                                type="button"
                                                onClick={clearFilters}
                                                className="mt-5 inline-flex min-h-[42px] items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-5 text-sm font-black text-black transition hover:border-[#414141]"
                                            >
                                                Clear Filters
                                            </button>
                                        )}
                                    />
                                ) : (
                                    <section className="overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
                                        {isLoading && (
                                            <p className="border-b border-[#d9d9d9] px-4 py-2 text-xs font-black uppercase tracking-widest text-[#414141] md:px-5">Refreshing...</p>
                                        )}
                                        <div className="divide-y divide-[#d9d9d9]">
                                            {visibleDocuments.map(({ doc }, index) => {
                                                const isNew = newItem?.kind === 'document' && getDocId(doc) === newItem.id;
                                                return (
                                                    // Wrapper carries the new-upload glow (rounded so the halo follows
                                                    // the corners; raised so neighbouring rows don't paint over it).
                                                    <div
                                                        key={doc.doc_id || doc.path || doc.storage_url || doc.storageUrl || `${doc.name}-${index}`}
                                                        ref={isNew ? (el) => el?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }) : undefined}
                                                        className={isNew ? 'relative z-[1] rounded-xl animate-dash-new-glow' : undefined}
                                                    >
                                                        <DocumentListItem
                                                            doc={doc}
                                                            onOpenImage={setDocumentPreview}
                                                            onOpenReport={handleOpenReportDoc}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {filteredDocuments.length > DOCUMENTS_PREVIEW_COUNT && (
                                            <div className="border-t border-[#d9d9d9] px-4 py-3 md:px-5">
                                                <ShowAllButton
                                                    count={filteredDocuments.length}
                                                    showAll={documentsShowAll}
                                                    onToggle={() => setDocumentsShowAll((value) => !value)}
                                                />
                                            </div>
                                        )}
                                    </section>
                                )}

                                {hasActiveFilters && (
                                    <div className="mt-3 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={clearFilters}
                                            className="inline-flex min-h-[36px] items-center justify-center rounded-xl border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26]"
                                        >
                                            Clear filters
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                        </div>
                    </>
                )}
            </div>

            {serviceFormOpen && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => { if (!serviceFormSaving) setServiceFormOpen(false); }}
                    />
                    <div className="relative w-full max-w-md bg-white rounded-[28px] shadow-2xl p-7 animate-in fade-in zoom-in-95 duration-200">
                        <div className="w-12 h-12 rounded-2xl bg-[#f5f5f5] flex items-center justify-center mb-4">
                            <WrenchIcon />
                        </div>
                        <h3 className="text-xl font-black text-black tracking-tight mb-1">Add to service history</h3>
                        <p className="text-sm font-semibold text-black/50 leading-relaxed mb-5">
                            Save completed work and any extra details to {activeVehicleLabel || 'this vehicle'}’s record.
                        </p>

                        <label className="block text-sm font-black text-black mb-1.5">Service performed</label>
                        <input
                            type="text"
                            value={serviceForm.title}
                            onChange={(e) => setServiceForm((f) => ({ ...f, title: e.target.value }))}
                            placeholder="e.g. Replaced AC compressor"
                            className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm font-medium text-black placeholder:text-black/30 focus:border-[#D81F26] focus:outline-none mb-4"
                        />

                        <label className="block text-sm font-black text-black mb-1.5">
                            Additional information <span className="font-semibold text-black/40">(optional)</span>
                        </label>
                        <textarea
                            value={serviceForm.notes}
                            onChange={(e) => setServiceForm((f) => ({ ...f, notes: e.target.value }))}
                            rows={3}
                            placeholder="Parts used, shop, cost, anything worth keeping…"
                            className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm font-medium text-black placeholder:text-black/30 focus:border-[#D81F26] focus:outline-none resize-none mb-4"
                        />

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div>
                                <label className="block text-sm font-black text-black mb-1.5">
                                    Odometer ({unitLabel(distanceUnit)}) <span className="font-semibold text-black/40">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={serviceForm.odometer}
                                    onChange={(e) => setServiceForm((f) => ({ ...f, odometer: e.target.value }))}
                                    placeholder={distanceUnit === 'km' ? 'e.g. 66000' : 'e.g. 41000'}
                                    className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm font-medium text-black placeholder:text-black/30 focus:border-[#D81F26] focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-black text-black mb-1.5">Service date</label>
                                <USDateInput
                                    value={serviceForm.date}
                                    max={new Date().toLocaleDateString('en-CA')}
                                    onChange={(iso) => setServiceForm((f) => ({ ...f, date: iso }))}
                                    className="w-full rounded-2xl border border-black/10 px-4 py-3 pr-10 text-sm font-medium text-black focus:border-[#D81F26] focus:outline-none"
                                />
                            </div>
                        </div>

                        {serviceWarnings.length > 0 && (
                            <div className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 p-4">
                                <p className="text-sm font-black text-amber-900 mb-1.5">Double-check the mileage</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    {serviceWarnings.map((w, i) => (
                                        <li key={i} className="text-sm font-semibold text-amber-800 leading-snug">
                                            {typeof w === 'string' ? w : (w?.message || w?.detail || '')}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {serviceFormError && (
                            <p className="text-sm font-bold text-rose-600 mb-4">{serviceFormError}</p>
                        )}

                        {serviceWarnings.length > 0 ? (
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setServiceWarnings([])}
                                    disabled={serviceFormSaving}
                                    className="flex-1 py-3 rounded-full border border-black/10 font-black uppercase tracking-widest text-xs text-black/60 hover:bg-black/5 transition-colors disabled:opacity-50"
                                >
                                    Fix the mileage
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleSaveServiceRecord(true)}
                                    disabled={serviceFormSaving}
                                    className="flex-1 py-3 rounded-full bg-amber-500 text-white font-black uppercase tracking-widest text-xs hover:bg-amber-600 transition-colors disabled:opacity-60"
                                >
                                    {serviceFormSaving ? 'Saving…' : 'Save anyway'}
                                </button>
                            </div>
                        ) : (
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setServiceFormOpen(false)}
                                    disabled={serviceFormSaving}
                                    className="flex-1 py-3 rounded-full border border-black/10 font-black uppercase tracking-widest text-xs text-black/60 hover:bg-black/5 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleSaveServiceRecord(false)}
                                    disabled={serviceFormSaving}
                                    className="flex-1 py-3 rounded-full bg-[#D81F26] text-white font-black uppercase tracking-widest text-xs hover:bg-[#B8181E] transition-colors disabled:opacity-60"
                                >
                                    {serviceFormSaving ? 'Saving…' : 'Save record'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <DocumentImageModal doc={documentPreview} onClose={() => setDocumentPreview(null)} />
        </div>
    );
};

export default GloveboxPage;
